import { types, getParent, flow, getRoot } from 'mobx-state-tree'
import buildPersistedChangesStore from '~/stores/models/persistedChanges'

import * as UserService from '~/services/user/index'
import { REQUEST_STATUSES } from '~/utilities/constants'

const PREFERENCES_KEYS = [
  'promotions',
  'newsletter',
  'trafficTicketNotificationsSms',
  'trafficTicketNotificationsEmail',
  'userReplyStopFromRobot',
  'userReplyStopFromRobotDate',
  'userReplyStopFromRobotPhone'
]

const UserCommunicationsPreferencesBaseModel = types
  .model('UserCommunicationsPreferences', {
    requestStatus: types.optional(types.string, REQUEST_STATUSES.init),
    saveStatus: types.optional(types.string, REQUEST_STATUSES.init),
    promotions: types.maybeNull(types.boolean),
    newsletter: types.maybeNull(types.boolean),
    trafficTicketNotificationsSms: types.maybeNull(types.boolean),
    trafficTicketNotificationsEmail: types.maybeNull(types.boolean),
    userReplyStopFromRobot: types.maybeNull(types.boolean),
    userReplyStopFromRobotDate: types.maybeNull(types.Date),
    userReplyStopFromRobotPhone: types.maybeNull(types.string),
    // NONE EDITABLE! PURELY FOR DISPLAY:
    transactionalEmails: true
  })
  .preProcessSnapshot(snapshot => {
    if(!snapshot) return null
    const parsedSnapshot = {}
    const keys = Object.keys(snapshot)
    keys.forEach(key => {
      switch (key) {
        case 'userReplyStopFromRobotDate':
          parsedSnapshot[key] = snapshot[key] ? new Date(snapshot[key]) : null
          break
        default:
          parsedSnapshot[key] = snapshot[key]
          break
      }
    })

    return parsedSnapshot
  })
  .views(self => ({
    get sessionToken() {
      return getParent(self).sessionToken
    },
    get isLoading() {
      return (
        self.requestStatus === REQUEST_STATUSES.loading ||
        self.saveStatus === REQUEST_STATUSES.loading
      )
    }
  }))
  .actions(self => ({
    togglePreference(key) {
      self[key] = !self[key]
    },
    setRequestStatus(newStatus) {
      self.requestStatus = newStatus
    },
    setSaveStatus(newStatus) {
      self.saveStatus = newStatus
    },
    loadIfNecessary() {
      if (self.requestStatus === REQUEST_STATUSES.init) {
        return self.fetch()
      }
      return Promise.resolve()
    },
    fetch: flow(function* fetch() {
      let success = false
      try {
        if (self.requestStatus === REQUEST_STATUSES.loading) {
          return true
        }
        self.setRequestStatus(REQUEST_STATUSES.loading)
        const preferences = yield UserService.fetchCommunicationsPreferences(
          self.sessionToken
        )
        self.setPreferences(preferences)
        success = true
        self.setRequestStatus(REQUEST_STATUSES.complete)
      } catch (error) {
        self.error(error)
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error fetching user preferences', error)
        }
        success = false
        self.setRequestStatus(REQUEST_STATUSES.error)
      }
      return success
    }),
    update: flow(function* fetch() {
      let success = false
      try {
        if (self.saveStatus === REQUEST_STATUSES.loading) {
          // self.success()
          return true
        }
        self.setSaveStatus(REQUEST_STATUSES.loading)
        const data = self.buildDataUpdateObject(PREFERENCES_KEYS)
        if (data) {
          const preferences = yield UserService.updateCommunicationsPreferences(
            data,
            self.sessionToken
          )
          self.setLastUpdate()
          self.setPreferences(preferences)
          self.success()
        }
        success = true
        self.setSaveStatus(REQUEST_STATUSES.complete)
      } catch (error) {
        self.error(error)
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error updating user preferences', error)
        }
        success = false
        self.setSaveStatus(REQUEST_STATUSES.error)
      }
      return success
    }),
    setPreferences(preferencesObject) {
      self.setLastUpdate(preferencesObject)
      Object.keys(preferencesObject).forEach(key => {
        if (PREFERENCES_KEYS.includes(key)) {
          self[key] = preferencesObject[key]
        }
      })
    },
    success() {
      self.showToast(
        'Communications preferences successfully updated',
        'SUCCESS'
      )
    },
    error(message) {
      self.showToast(`Could not update preferences: ${message}`, 'ERROR')
    },
    showToast(message, type) {
      const all = getRoot(self)
      all.ui.toasts.addToast({
        message,
        type
      })
    }
  }))

const PersistedChangesStore = buildPersistedChangesStore()

const UserCommunicationsPreferences = types.compose(
  UserCommunicationsPreferencesBaseModel,
  PersistedChangesStore
)

export default UserCommunicationsPreferences
